// ==================== GURUKUL — TEAMS (admin) ====================
// Team overview + create, coordinator moves (with families), learner reassignment,
// pending LMS updates and the auto-add settings. Reuses the same card / table / form
// classes as the existing Team screen so it looks and behaves the same way.
import { showToast, showConfirm } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}
const post = (path, body) => api(path, { method: 'POST', body: JSON.stringify(body) });

let teamsCache = [];
let coordinatorsCache = [];
let leadPairs = [];

export function teamsSectionHtml() {
  return `
    <style>
      @keyframes gkspin { to { transform: rotate(360deg); } }
      .gk-ctl-row { display:flex; gap:6px; align-items:stretch; }
      .gk-ctl-row select { height:72px; min-width:170px; flex:1; }
      .gk-go { width:44px; height:72px; padding:0; background:#16a34a; color:#fff; border:none; border-radius:6px; font-size:1.5rem; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; }
      .gk-go:hover { background:#15803d; }
      .gk-filter-row { display:flex; flex-wrap:wrap; gap:10px; align-items:flex-end; margin-bottom:12px; }
      .gk-filter-row .form-group { margin:0; }
      .gk-pager { display:flex; gap:10px; align-items:center; justify-content:flex-end; margin-top:10px; }
      .gk-spinner { display:inline-block; width:18px; height:18px; border:3px solid #ccc; border-top-color:#2D6A4F; border-radius:50%; animation:gkspin 0.8s linear infinite; vertical-align:middle; margin-right:8px; }
    </style>
    <div class="card" id="gk-sec-teams">
      <h2>Teams</h2>
      <p class="page-intro">Every Gurukul team always has a lead. A coordinator can only leave a team once someone else leads it.</p>
      <div class="table-container">
        <table class="table-stack-mobile">
          <thead><tr><th>Team</th><th>Prefix</th><th>Lead(s)</th><th>Members</th><th>Learners</th><th></th></tr></thead>
          <tbody id="gkTeamsBody"><tr><td colspan="6" class="empty-state">Loading…</td></tr></tbody>
        </table>
      </div>
      <h3 style="margin-top:16px;">Create a team</h3>
      <p class="page-intro">You become the team's lead until you assign a coordinator.</p>
      <form id="gkCreateTeamForm" class="form-stack">
        <div class="form-grid-row">
          <div class="form-group"><label for="gkNewTeamName">Team name</label><input type="text" id="gkNewTeamName" required></div>
          <div class="form-group"><label for="gkNewTeamPrefix">Prefix (2-8 letters/digits)</label><input type="text" id="gkNewTeamPrefix" required maxlength="8"></div>
        </div>
        <button type="submit" id="gkCreateTeamBtn">Create team</button>
      </form>
    </div>

    <div id="gkTeamModal" class="modal"><div class="modal-content" style="max-width:760px;padding:24px 30px;"><div id="gkTeamDetail"></div></div></div>

    <div class="card" id="gk-sec-lms-updates">
      <h2>LMS updates pending</h2>
      <p class="page-intro">Learners whose team (and so coordinator) changed. The LMS relationship still has to be updated.</p>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Learner</th><th>From</th><th>To</th><th>Reason</th><th>Since</th></tr></thead>
        <tbody id="gkLmsBody"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
    </div>

    <div class="card" id="gk-sec-learners">
      <h2>Learners</h2>
      <div class="gk-filter-row">
        <div class="form-group" style="flex:1;min-width:220px;"><label for="gkLSearch">Search</label>
          <input type="text" id="gkLSearch" placeholder="Learner ID or guardian name/email" maxlength="80"></div>
        <div class="form-group"><label for="gkLTeam">Team</label><select id="gkLTeam"><option value="">All teams</option></select></div>
        <div class="form-group"><label for="gkLStatus">Status</label>
          <select id="gkLStatus"><option value="active" selected>Active</option><option value="retired">Retired</option><option value="all">All</option></select></div>
        <button type="button" id="gkLSearchBtn">Search</button>
      </div>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Learner</th><th>Guardians</th><th>Primary guardian</th><th>Team</th><th>Move to</th></tr></thead>
        <tbody id="gkLearnersBody"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
      <div class="gk-pager"><span id="gkLInfo"></span><button type="button" class="secondary" id="gkLPrev">Prev</button><button type="button" class="secondary" id="gkLNext">Next</button></div>
    </div>

    <div class="card" id="gk-sec-auto-add">
      <h2>Auto-add settings</h2>
      <p class="page-intro">People who receive a role join the chosen team automatically.</p>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Role</th><th>Team</th><th>Level</th><th>Status</th><th></th></tr></thead>
        <tbody id="gkRulesBody"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
      <h3 style="margin-top:16px;">Add a rule</h3>
      <form id="gkRuleForm" class="form-stack">
        <div class="form-grid-row">
          <div class="form-group"><label for="gkRuleRole">Role</label><select id="gkRuleRole"></select></div>
          <div class="form-group"><label for="gkRuleTeam">Team</label><select id="gkRuleTeam"></select></div>
          <div class="form-group"><label for="gkRuleLevel">Level</label>
            <select id="gkRuleLevel"><option value="member">Member (OPS)</option><option value="view">View only</option></select></div>
        </div>
        <label style="display:flex;align-items:center;gap:6px;font-weight:normal;margin-bottom:8px;">
          <input type="checkbox" id="gkRuleExisting"> Also add people who already hold this role
        </label>
        <button type="submit" id="gkRuleBtn">Add rule</button>
      </form>
    </div>

    <div id="gkMoveModal" class="modal">
      <div class="modal-content" style="max-width:640px;padding:24px 30px;">
        <div id="gkMoveBody"></div>
      </div>
    </div>
  `;
}

// ───────────────────────── search + dropdown helper ─────────────────────────

function searchSelectHtml(id, placeholder) {
  return `<input type="text" id="${id}Search" placeholder="${esc(placeholder)}" style="width:100%;margin-bottom:6px;">
          <select id="${id}" style="width:100%;"></select>`;
}
function wireSearchSelect(id, options) {
  const input = document.getElementById(id + 'Search');
  const select = document.getElementById(id);
  const render = () => {
    const q = (input.value || '').trim().toLowerCase();
    const cur = select.value;
    const list = options.filter(o => !q || o.label.toLowerCase().includes(q));
    select.innerHTML = '<option value="">Choose…</option>' + list.map(o => `<option value="${esc(o.value)}"${o.value === cur ? ' selected' : ''}>${esc(o.label)}</option>`).join('');
  };
  input.addEventListener('input', render);
  render();
}

// ───────────────────────────── teams overview ───────────────────────────────

async function loadTeams() {
  const tbody = document.getElementById('gkTeamsBody');
  try {
    const { teams, coordinators } = await api('/api/gurukul/teams');
    teamsCache = teams;
    coordinatorsCache = coordinators;
    tbody.innerHTML = teams.map(t => `
      <tr>
        <td data-label="Team">${esc(t.name)}${t.is_system ? ' <em>(system)</em>' : ''}${t.is_pool ? ' <em>(staff pool)</em>' : ''}</td>
        <td data-label="Prefix">${esc(t.prefix || '')}</td>
        <td data-label="Lead(s)">${t.leads.length ? t.leads.map((l, i) => esc(l.name || l.email) + (t.leads.length > 1 ? (i === 0 ? ' (primary)' : ' (secondary)') : '')).join(', ') : '—'}</td>
        <td data-label="Members">${t.members}</td>
        <td data-label="Learners">${t.learners}</td>
        <td data-label="" class="action-buttons"><button type="button" class="gk-team-manage" data-id="${t.id}">Manage</button></td>
      </tr>`).join('');
    tbody.querySelectorAll('.gk-team-manage').forEach(b => b.addEventListener('click', () => openTeamDetail(b.dataset.id)));
    fillRuleSelects();
  } catch (err) {
    tbody.innerHTML = `<tr><td colspan="6" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`;
  }
}

async function openTeamDetail(teamId) {
  const team = teamsCache.find(t => t.id === teamId);
  const card = document.getElementById('gkTeamDetail');
  document.getElementById('gkTeamModal').classList.add('active');
  card.innerHTML = `<h2>${esc(team.name)}</h2><p class="empty-state">Loading…</p>`;
  const { members } = await api('/api/gurukul/teams?members=' + encodeURIComponent(teamId));
  let teamLearners = [];
  try { teamLearners = (await api('/api/gurukul/learner-team')).learners.filter(l => l.team_id === teamId && l.status !== 'retired'); } catch (e) { /* shown empty */ }
  const movable = members.filter(m => m.is_coordinator);
  const addable = coordinatorsCache.filter(c => !members.some(m => m.id === c.id && m.access_level === 'lead'));
  const leads = members.filter(m => m.access_level === 'lead');
  card.innerHTML = `
    <h2>${esc(team.name)}</h2>
    <h3>Coordinators</h3>
    <p class="page-intro">${leads.length ? leads.map(m => esc(m.name || m.email) + ' — ' + (m.lead_rank === 1 ? 'primary' : 'secondary')).join('; ') : 'No lead yet.'}</p>
    <h3>People</h3>
    <div class="table-container"><table class="table-stack-mobile">
      <thead><tr><th>Person</th><th>Email</th><th>Level</th><th></th></tr></thead>
      <tbody>${members.map(m => `
        <tr>
          <td data-label="Person">${esc(m.name || m.email)}</td>
          <td data-label="Email">${esc(m.email)}</td>
          <td data-label="Level">${m.access_level === 'lead' ? 'Lead (OPL) · ' + (m.lead_rank === 1 ? 'Primary' : 'Secondary') : m.access_level === 'oht' ? 'Head (OPH)' : 'Member (OPS)'}${m.is_coordinator ? ' · coordinator' : ''}</td>
          <td data-label="" class="action-buttons">${m.access_level === 'lead' && m.lead_rank > 1 ? `<button type="button" class="gk-make-primary" data-user="${m.id}">Make primary</button> ` : ''}${m.access_level === 'lead' ? `<button type="button" class="gk-make-member secondary" data-user="${m.id}" data-name="${esc(m.name || m.email)}">Make member</button> ` : ''}<button type="button" class="gk-remove-member danger" data-user="${m.id}" data-name="${esc(m.name || m.email)}">Remove from team</button> ${movable.some(x => x.id === m.id) ? `<button type="button" class="gk-move-btn" data-user="${m.id}" data-name="${esc(m.name || m.email)}">Move…</button>` : ''}</td>
        </tr>`).join('')}</tbody>
    </table></div>
    <h3 style="margin-top:16px;">Learners (${teamLearners.length})</h3>
    <p class="page-intro">${teamLearners.length ? teamLearners.map(l => esc(l.learner_id)).join(', ') : 'No active learners in this team.'}</p>
    ${team.is_system ? '' : `
    <h3 style="margin-top:16px;">Add a coordinator</h3>
    <div class="form-grid-row">
      <div class="form-group">${searchSelectHtml('gkAddLead', 'Search coordinators…')}</div>
    </div>
    <div class="form-group">
      <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkLeadRank" value="primary" ${members.some(m => m.access_level === 'lead') ? '' : 'checked'}> Primary — ${leads.length ? 'the current primary (' + esc(leads.find(m => m.lead_rank === 1)?.name || 'lead') + ') stays a lead but becomes secondary' : 'first lead of this team'}</label>
      <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkLeadRank" value="secondary" ${members.some(m => m.access_level === 'lead') ? 'checked' : ''}> Secondary — the current primary stays primary</label>
    </div>
    <button type="button" id="gkAddLeadBtn">Add coordinator</button>`}
    <button type="button" class="secondary" id="gkCloseDetail" style="margin-left:8px;">Close</button>`;
  card.querySelectorAll('.gk-move-btn').forEach(b => b.addEventListener('click', () => { document.getElementById('gkTeamModal').classList.remove('active'); openMoveDialog(teamId, b.dataset.user, b.dataset.name); }));
  card.querySelectorAll('.gk-make-primary').forEach(b => b.addEventListener('click', async () => {
    try {
      await post('/api/gurukul/teams', { action: 'set_primary', team_id: teamId, user_id: b.dataset.user });
      showToast('Primary lead changed', 'success');
      await loadTeams();
      openTeamDetail(teamId);
    } catch (err) { showToast(err.message, 'error'); }
  }));
  card.querySelectorAll('.gk-make-member').forEach(b => b.addEventListener('click', async () => {
    if (!(await showConfirm('Remove <strong>' + b.dataset.name + '</strong> as coordinator of <strong>' + esc(team.name) + '</strong>? They stay in the team as a normal member.'))) return;
    try {
      await post('/api/gurukul/teams', { action: 'make_member', team_id: teamId, user_id: b.dataset.user });
      showToast('Now a member of the team', 'success');
      await loadTeams();
      openTeamDetail(teamId);
    } catch (err) { showToast(err.message, 'error'); }
  }));
  card.querySelectorAll('.gk-remove-member').forEach(b => b.addEventListener('click', async () => {
    if (!(await showConfirm('Remove <strong>' + b.dataset.name + '</strong> from <strong>' + esc(team.name) + '</strong> completely? If they are in no other Gurukul team they are placed in Unallocated.'))) return;
    try {
      const out = await post('/api/gurukul/teams', { action: 'remove_member', team_id: teamId, user_id: b.dataset.user });
      showToast(out.placedInUnallocated ? 'Removed — now in Unallocated' : 'Removed from the team', 'success');
      await loadTeams();
      openTeamDetail(teamId);
    } catch (err) { showToast(err.message, 'error'); }
  }));
  document.getElementById('gkCloseDetail').addEventListener('click', () => document.getElementById('gkTeamModal').classList.remove('active'));
  if (!team.is_system) {
    wireSearchSelect('gkAddLead', addable.map(c => ({ value: c.id, label: `${c.name || c.email} (${c.email})` })));
    document.getElementById('gkAddLeadBtn').addEventListener('click', async () => {
      const userId = document.getElementById('gkAddLead').value;
      if (!userId) { showToast('Choose a coordinator', 'error'); return; }
      try {
        await post('/api/gurukul/teams', { action: 'add_lead', team_id: teamId, user_id: userId, primary: document.querySelector('input[name="gkLeadRank"]:checked')?.value === 'primary' });
        showToast('Lead added', 'success');
        await loadTeams();
        openTeamDetail(teamId);
      } catch (err) { showToast(err.message, 'error'); }
    });
  }
}

// ─────────────────────────────── move dialog ────────────────────────────────

function closeModal() { document.getElementById('gkMoveModal').classList.remove('active'); }

async function openMoveDialog(fromTeamId, coordinatorId, coordinatorName) {
  const from = teamsCache.find(t => t.id === fromTeamId);
  const modal = document.getElementById('gkMoveModal');
  const body = document.getElementById('gkMoveBody');
  modal.classList.add('active');
  try { leadPairs = (await api('/api/gurukul/team-leads')).leads; } catch (e) { leadPairs = []; }

  const destOptions = teamsCache.filter(t => t.id !== fromTeamId);
  body.innerHTML = `
    <h2>Move ${esc(coordinatorName)}</h2>
    <p class="page-intro">From <strong>${esc(from.name)}</strong></p>
    <div class="form-group"><label for="gkMoveDest">Move to team</label>
      <select id="gkMoveDest">${destOptions.map(t => `<option value="${t.id}">${esc(t.name)}${t.is_system ? ' (system)' : ''}</option>`).join('')}</select></div>
    <div id="gkMoveQuestions"></div>
    <div class="btn-group" style="margin-top:12px;">
      <button type="button" id="gkMoveNext">Next</button>
      <button type="button" class="secondary" id="gkMoveCancel">Cancel</button>
    </div>`;
  document.getElementById('gkMoveCancel').addEventListener('click', closeModal);
  document.getElementById('gkMoveNext').addEventListener('click', async () => {
    const toTeamId = document.getElementById('gkMoveDest').value;
    let preview;
    try {
      preview = await post('/api/gurukul/team-move', { action: 'preview', coordinator_id: coordinatorId, from_team_id: fromTeamId, to_team_id: toTeamId });
    } catch (err) { showToast(err.message, 'error'); return; }
    renderMoveQuestions(preview, { coordinatorId, fromTeamId, toTeamId });
  });
}

function renderMoveQuestions(pv, ctx) {
  const box = document.getElementById('gkMoveQuestions');
  const nParents = pv.families.length;
  const nLearners = pv.families.reduce((n, f) => n + f.learners.length, 0);
  const candidates = coordinatorsCache
    .filter(c => c.id !== ctx.coordinatorId)
    .map(c => ({ value: c.id, label: `${c.name || c.email} (${c.email})` }));
  const teamChoices = leadPairs
    .filter(p => p.team_id !== ctx.fromTeamId && p.user_id !== ctx.coordinatorId)
    .map(p => ({ value: p.team_id, label: `${p.name || p.email} — ${p.team_name}` }));
  const destSystem = pv.to.is_system;

  box.innerHTML = `
    ${pv.needsReplacement ? `
      <div class="card" style="margin:12px 0;">
        <h3>${esc(pv.from.name)} would have no lead</h3>
        <p>Assign a new coordinator to ${esc(pv.from.name)}?</p>
        <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkRepl" value="yes" checked> Yes — choose a coordinator</label>
        <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkRepl" value="no"> No — the OPD becomes lead (and is told by message)</label>
        <div id="gkReplPick" style="margin-top:8px;">${searchSelectHtml('gkReplSel', 'Search coordinators…')}</div>
      </div>` : ''}
    ${nParents ? `
      <div class="card" style="margin:12px 0;">
        <h3>Families in ${esc(pv.from.name)}</h3>
        <p>Move the associated ${nParents} parent(s) and ${nLearners} learner(s) too?</p>
        <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkFam" value="yes" ${destSystem ? 'disabled' : 'checked'}> Yes — move them with the coordinator to ${esc(pv.to.name)}${destSystem ? ' (not possible: system team)' : ''}</label>
        <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkFam" value="no" ${destSystem ? 'checked' : ''}> No</label>
        <div id="gkFamNo" style="display:none;margin-top:8px;">
          <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkFamHow" value="one" checked> One coordinator takes them all</label>
          <label style="display:flex;gap:6px;font-weight:normal;"><input type="radio" name="gkFamHow" value="many"> Different coordinators (choose per parent)</label>
          <div id="gkFamOne" style="margin-top:8px;">${searchSelectHtml('gkFamOneSel', 'Search coordinators…')}</div>
          <div id="gkFamMany" style="display:none;margin-top:8px;">
            ${pv.families.map((f, i) => `
              <div style="display:flex;gap:8px;align-items:center;margin-bottom:6px;">
                <div style="flex:1;"><strong>${esc(f.name || f.email)}</strong><br><small>${f.learners.length ? esc(f.learners.join(', ')) : 'no learners yet'}</small></div>
                <select class="gk-fam-select" data-parent="${f.parent_id}" style="flex:1;"></select>
              </div>`).join('')}
          </div>
        </div>
      </div>` : ''}
    <div class="btn-group"><button type="button" id="gkMoveConfirm">Confirm move</button></div>`;

  if (pv.needsReplacement) {
    wireSearchSelect('gkReplSel', candidates);
    box.querySelectorAll('input[name="gkRepl"]').forEach(r => r.addEventListener('change', () => {
      document.getElementById('gkReplPick').style.display = box.querySelector('input[name="gkRepl"]:checked').value === 'yes' ? '' : 'none';
    }));
  }
  if (nParents) {
    wireSearchSelect('gkFamOneSel', teamChoices);
    box.querySelectorAll('.gk-fam-select').forEach(sel => {
      sel.innerHTML = '<option value="">Choose…</option>' + teamChoices.map(o => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join('');
    });
    const refreshFam = () => {
      const moveYes = box.querySelector('input[name="gkFam"]:checked').value === 'yes';
      document.getElementById('gkFamNo').style.display = moveYes ? 'none' : '';
      const many = box.querySelector('input[name="gkFamHow"]:checked').value === 'many';
      document.getElementById('gkFamOne').style.display = many ? 'none' : '';
      document.getElementById('gkFamMany').style.display = many ? '' : 'none';
    };
    box.querySelectorAll('input[name="gkFam"], input[name="gkFamHow"]').forEach(r => r.addEventListener('change', refreshFam));
    refreshFam();
  }

  document.getElementById('gkMoveConfirm').addEventListener('click', async () => {
    const payload = { coordinator_id: ctx.coordinatorId, from_team_id: ctx.fromTeamId, to_team_id: ctx.toTeamId, move_families: false, assignments: [], replacement: null };
    if (pv.needsReplacement) {
      if (box.querySelector('input[name="gkRepl"]:checked').value === 'yes') {
        const id = document.getElementById('gkReplSel').value;
        if (!id) { showToast('Choose the replacement coordinator', 'error'); return; }
        payload.replacement = { mode: 'coordinator', user_id: id };
      } else payload.replacement = { mode: 'opd' };
    }
    if (nParents) {
      const yes = box.querySelector('input[name="gkFam"]:checked').value === 'yes';
      if (yes) {
        payload.move_families = true;
        payload.assignments = pv.families.map(f => ({ parent_id: f.parent_id, team_id: ctx.toTeamId }));
      } else {
        const how = box.querySelector('input[name="gkFamHow"]:checked').value;
        payload.move_families = true;
        if (how === 'one') {
          const team = document.getElementById('gkFamOneSel').value;
          if (!team) { showToast('Choose who takes the families', 'error'); return; }
          payload.assignments = pv.families.map(f => ({ parent_id: f.parent_id, team_id: team }));
        } else {
          const sels = [...box.querySelectorAll('.gk-fam-select')];
          if (sels.some(s => !s.value)) { showToast('Choose a coordinator for every parent', 'error'); return; }
          payload.assignments = sels.map(s => ({ parent_id: s.dataset.parent, team_id: s.value }));
        }
      }
    }
    runMove(payload);
  });
}

async function runMove(payload) {
  const body = document.getElementById('gkMoveBody');
  body.innerHTML = `<h2>Moving…</h2><p id="gkMoveProgress"><span class="gk-spinner"></span>Membership is being set up…</p>`;
  let jobId;
  try {
    const started = await post('/api/gurukul/team-move', { action: 'start', ...payload });
    jobId = started.job_id;
  } catch (err) {
    body.innerHTML = `<h2>Move failed</h2><p>${esc(err.message)}</p><button type="button" id="gkMoveClose">Close</button>`;
    document.getElementById('gkMoveClose').addEventListener('click', closeModal);
    return;
  }
  const execute = async () => {
    body.innerHTML = `<h2>Moving…</h2><p id="gkMoveProgress"><span class="gk-spinner"></span>Membership is being set up…</p>`;
    const timer = setInterval(async () => {
      try {
        const { job } = await api('/api/gurukul/team-move?id=' + jobId);
        const el = document.getElementById('gkMoveProgress');
        if (el && job.status === 'running') {
          el.innerHTML = `<span class="gk-spinner"></span>Step ${Math.min(job.completed_steps + 1, job.total_steps)} of ${job.total_steps} in progress — ${esc(job.step_label || '')}`;
        }
      } catch (e) { /* keep polling */ }
    }, 600);
    let job;
    try {
      job = (await post('/api/gurukul/team-move', { action: 'run', job_id: jobId })).job;
    } catch (err) {
      job = { status: 'failed', error: err.message };
    }
    clearInterval(timer);
    if (job.status === 'done') {
      body.innerHTML = `<h2>Move complete</h2><p>All ${job.total_steps} steps finished.</p><button type="button" id="gkMoveClose">Close</button>`;
      document.getElementById('gkMoveClose').addEventListener('click', closeModal);
      await Promise.all([loadTeams(), loadLearners(), loadLmsUpdates()]);
    } else {
      body.innerHTML = `<h2>Move stopped</h2><p>${esc(job.error || 'Something went wrong')}</p>
        <p>Nothing is lost — the steps already finished stay done and the rest can be retried.</p>
        <div class="btn-group"><button type="button" id="gkMoveRetry">Retry</button><button type="button" class="secondary" id="gkMoveClose">Close</button></div>`;
      document.getElementById('gkMoveRetry').addEventListener('click', execute);
      document.getElementById('gkMoveClose').addEventListener('click', () => { closeModal(); loadTeams(); });
    }
  };
  execute();
}

// ───────────────────────── learners, LMS updates, rules ─────────────────────

async function loadLmsUpdates() {
  const tbody = document.getElementById('gkLmsBody');
  try {
    const { updates } = await api('/api/gurukul/learner-team?view=lms-updates');
    tbody.innerHTML = updates.length ? updates.map(u => `
      <tr>
        <td data-label="Learner">${esc(u.learner_id)}</td><td data-label="From">${esc(u.from_team || '—')}</td>
        <td data-label="To">${esc(u.to_team || '—')}</td><td data-label="Reason">${esc(u.reason.replace('_', ' '))}</td>
        <td data-label="Since">${new Date(u.created_at).toLocaleDateString()}</td>
      </tr>`).join('') : '<tr><td colspan="5" class="empty-state">Nothing pending.</td></tr>';
  } catch (err) { tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`; }
}

let learnerPage = 1;

async function loadLearners() {
  const tbody = document.getElementById('gkLearnersBody');
  const qs = new URLSearchParams({
    q: document.getElementById('gkLSearch').value.trim(),
    team: document.getElementById('gkLTeam').value,
    status: document.getElementById('gkLStatus').value,
    page: String(learnerPage), size: '25',
  });
  try {
    const { learners, total, page, pages, size } = await api('/api/gurukul/learner-team?' + qs.toString());
    learnerPage = page;
    const real = teamsCache.filter(t => !t.is_system);
    const filterSel = document.getElementById('gkLTeam');
    if (filterSel.options.length <= 1) {
      filterSel.innerHTML = '<option value="">All teams</option>' + teamsCache.map(t => `<option value="${t.id}">${esc(t.name)}</option>`).join('');
    }
    document.getElementById('gkLInfo').textContent = total ? `${(page - 1) * size + 1}–${Math.min(page * size, total)} of ${total}` : '0 learners';
    document.getElementById('gkLPrev').disabled = page <= 1;
    document.getElementById('gkLNext').disabled = page >= pages;
    tbody.innerHTML = learners.length ? learners.map(l => `
      <tr>
        <td data-label="Learner">${esc(l.learner_id)}${l.status === 'retired' ? ' <em>(retired)</em>' : ''}</td>
        <td data-label="Guardians">${l.guardians.length ? l.guardians.map(g => esc(g.name) + (g.is_primary ? ' ★' : '')).join(', ') : '—'}</td>
        <td data-label="Primary guardian">${l.status === 'retired' ? '—' : `<div class="gk-ctl-row"><select class="gk-primary-sel" data-id="${l.id}">${l.guardians.map(g => `<option value="${g.id}"${g.is_primary ? ' selected' : ''}>${esc(g.name)}</option>`).join('')}</select><button type="button" class="gk-go gk-primary-set" data-id="${l.id}" title="Make primary">✓</button></div>`}</td>
        <td data-label="Team">${esc(l.team_name || 'not set')}</td>
        <td data-label="Move to">${l.status === 'retired' ? '—' : `<div class="gk-ctl-row"><select class="gk-learner-team" data-id="${l.id}">${'<option value="">Choose…</option>' + real.filter(t => t.id !== l.team_id).map(t => `<option value="${t.id}">${esc(t.name)}</option>`).join('')}</select><button type="button" class="gk-go gk-learner-move" data-id="${l.id}" title="Move learner">→</button></div>`}</td>
      </tr>`).join('') : '<tr><td colspan="5" class="empty-state">No learners match.</td></tr>';
    tbody.querySelectorAll('.gk-primary-set').forEach(b => b.addEventListener('click', async () => {
      const sel = tbody.querySelector(`.gk-primary-sel[data-id="${b.dataset.id}"]`);
      try {
        const pv = await post('/api/gurukul/learner-team', { action: 'set_primary', learner_id: b.dataset.id, guardian_id: sel.value, preview: true });
        if (pv.already_primary) { showToast('That guardian is already the primary', 'info'); return; }
        const msg = pv.moves
          ? 'Make this guardian primary? The child will move to their team (' + esc(pv.new_team) + '), and an LMS update will be recorded.'
          : 'Make this guardian primary? The child stays in the same team.';
        if (!(await showConfirm(msg))) return;
        await post('/api/gurukul/learner-team', { action: 'set_primary', learner_id: b.dataset.id, guardian_id: sel.value });
        showToast('Primary guardian changed', 'success');
        await Promise.all([loadTeams(), loadLearners(), loadLmsUpdates()]);
      } catch (err) { showToast(err.message, 'error'); }
    }));
    tbody.querySelectorAll('.gk-learner-move').forEach(b => b.addEventListener('click', async () => {
      const sel = tbody.querySelector(`.gk-learner-team[data-id="${b.dataset.id}"]`);
      if (!sel.value) { showToast('Choose a team', 'error'); return; }
      const teamName = sel.options[sel.selectedIndex].text;
      if (!(await showConfirm('Move this learner to ' + esc(teamName) + '? An LMS update will be recorded.'))) return;
      try {
        await post('/api/gurukul/learner-team', { learner_id: b.dataset.id, team_id: sel.value });
        showToast('Learner moved — LMS update recorded', 'success');
        await Promise.all([loadTeams(), loadLearners(), loadLmsUpdates()]);
      } catch (err) { showToast(err.message, 'error'); }
    }));
  } catch (err) { tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`; }
}

function fillRuleSelects() {
  const role = document.getElementById('gkRuleRole');
  const team = document.getElementById('gkRuleTeam');
  if (team) team.innerHTML = teamsCache.map(t => `<option value="${t.id}">${esc(t.name)}${t.is_system ? ' (system)' : ''}</option>`).join('');
  if (role && !role.options.length) {
    ['coordinator', 'parent', 'learner', 'tester', 'tech_team', 'lms_team'].forEach(r => role.add(new Option(r, r)));
  }
}

async function loadRules() {
  const tbody = document.getElementById('gkRulesBody');
  try {
    const { rules } = await api('/api/gurukul/role-team-defaults');
    tbody.innerHTML = rules.length ? rules.map(r => `
      <tr>
        <td data-label="Role">${esc(r.role_code)}</td><td data-label="Team">${esc(r.team_name)}</td>
        <td data-label="Level">${esc(r.access_level)}</td><td data-label="Status">${r.is_active ? 'Active' : 'Paused'}</td>
        <td data-label="" class="action-buttons">
          <button type="button" class="gk-rule-toggle" data-id="${r.id}">${r.is_active ? 'Pause' : 'Resume'}</button>
          <button type="button" class="gk-rule-del" data-id="${r.id}">Delete</button>
        </td>
      </tr>`).join('') : '<tr><td colspan="5" class="empty-state">No rules.</td></tr>';
    tbody.querySelectorAll('.gk-rule-toggle').forEach(b => b.addEventListener('click', async () => { await post('/api/gurukul/role-team-defaults', { action: 'toggle', id: b.dataset.id }); loadRules(); }));
    tbody.querySelectorAll('.gk-rule-del').forEach(b => b.addEventListener('click', async () => { await post('/api/gurukul/role-team-defaults', { action: 'delete', id: b.dataset.id }); loadRules(); }));
  } catch (err) { tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`; }
}

export async function initTeamsSection() {
  document.getElementById('gkCreateTeamForm')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = document.getElementById('gkCreateTeamBtn');
    btn.disabled = true;
    try {
      await post('/api/gurukul/teams', { name: document.getElementById('gkNewTeamName').value, prefix: document.getElementById('gkNewTeamPrefix').value });
      showToast('Team created — you are its lead until you assign a coordinator', 'success');
      e.target.reset();
      await loadTeams();
    } catch (err) { showToast(err.message, 'error'); } finally { btn.disabled = false; }
  });
  document.getElementById('gkRuleForm')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    try {
      const r = await post('/api/gurukul/role-team-defaults', {
        action: 'add', role_code: document.getElementById('gkRuleRole').value, team_id: document.getElementById('gkRuleTeam').value,
        access_level: document.getElementById('gkRuleLevel').value, apply_existing: document.getElementById('gkRuleExisting').checked,
      });
      showToast(r.added ? `Rule added; ${r.added} existing people joined` : 'Rule added', 'success');
      loadRules();
    } catch (err) { showToast(err.message, 'error'); }
  });
  const reload = () => { learnerPage = 1; loadLearners(); };
  document.getElementById('gkLSearchBtn').addEventListener('click', reload);
  document.getElementById('gkLSearch').addEventListener('keydown', e => { if (e.key === 'Enter') reload(); });
  ['gkLTeam', 'gkLStatus'].forEach(id => document.getElementById(id).addEventListener('change', reload));
  document.getElementById('gkLPrev').addEventListener('click', () => { learnerPage = Math.max(learnerPage - 1, 1); loadLearners(); });
  document.getElementById('gkLNext').addEventListener('click', () => { learnerPage += 1; loadLearners(); });
  await loadTeams();
  await Promise.all([loadLearners(), loadLmsUpdates(), loadRules()]);
}
